import { defineStore } from 'pinia'
import { ref, computed, markRaw } from 'vue'
import { omit, get, has, debounce, defaults, throttle } from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import convert from 'convert-units'
import { NEW_AREA } from '@/constants.js'
import { shapeDefaults } from '@/constants.js'
import appConfig from '@/app-config.yml'
import { ShapeCommandQueue } from './shapeCommands.js'

const { units, default_units, base_units } = appConfig

function roundTo(number, places) {
  return +(Math.round(number + "e+" + places)  + "e-" + places);
}

export const useMainStore = defineStore('main', () => {
  // ===========================
  // State
  // ===========================
  const mode = ref('move')
  const toolType = ref('area')
  const imperial = ref(true)
  const primaryUnits = ref({})
  const activeStatusTab = ref(0)
  const address = ref('')
  const displayAddress = ref('')

  // NEW: Non-reactive Map for shape data + version counter for change tracking
  // This replaces the reactive shapes array to prevent deep reactivity issues
  const shapesData = markRaw(new Map()) // Non-reactive Map<indexID, shapeState>
  const shapesVersion = ref(0) // Increment this to trigger re-renders

  const lastMapClickEvent = ref(null)
  const tutorialModalVisible = ref(false)
  const globalZIndex = ref(0)
  const mapCenter = ref({})
  const meta = ref({})
  const mapDrag = ref(false)
  const toolbarsHidden = ref(false)
  const mapZoom = ref({})
  const mapEditingEnabled = ref(true)
  const projectName = ref('')
  const currentControlOverlay = ref(null)
  const loading = ref(false)
  const loadingMessage = ref('')
  const appClicked = ref(false)
  const utm = ref({})
  const uuid = ref(null)
  const lastSaveTime = ref(Date.now())

  // NEW: Initialize command queue for shape updates
  const commandQueue = new ShapeCommandQueue({
    shapesData,
    shapesVersion,
    triggerSave: null // Will be set after triggerSave is defined
  })

  // NEW: Helper function (not computed!) to get shapes as array from Map
  // This is NOT reactive to avoid recursion
  const getShapesArray = () => {
    return Array.from(shapesData.values())
  }

  // ===========================
  // Getters (Computed)
  // ===========================
  const shapesWithConversions = computed(() => {
    // Force recomputation when version changes
    shapesVersion.value // eslint-disable-line no-unused-expressions

    // Use helper function to avoid reactive traversal
    const shapesArray = getShapesArray()

    return shapesArray.map(s => {
      const sType = get(s, 'type', 'area')
      const unit = primaryUnits.value[sType] || units.find(u => u.name == default_units[sType])

      const baseDistanceUnit = base_units['distance']
      const distanceUnit = primaryUnits.value['distance'] || units.find(u => u.name == default_units['distance'])

      const baseUnitName = base_units[sType]
      const segmentLengths = Array.isArray(s.segmentLengths) ? s.segmentLengths : []

      return {
        ...s,
        convertedCalculated: roundTo(convert(s.calculated).from(baseUnitName).to(unit.name), unit.decimals),
        convertedSegmentLengths: segmentLengths.map(l => roundTo(convert(l).from(baseDistanceUnit).to(distanceUnit.name), distanceUnit.decimals)),
        distanceSuffix: distanceUnit.suffix,
        suffix: unit.suffix
      }
    })
  })

  const measureSuffix = computed(() => primaryUnit.value.suffix)

  const imperialStatus = computed(() => imperial.value ? true : false)

  const lastMapClickLatLng = computed(() => {
    if (lastMapClickEvent.value) {
      let { lat, lng } = lastMapClickEvent.value.latLng

      if (typeof lat === 'function') {
        lat = lat()
        lng = lng()
      }

      return new google.maps.LatLng(lat, lng)
    }
    return false
  })

  const largestShape = computed(() => {
    // Force recomputation when version changes
    shapesVersion.value // eslint-disable-line no-unused-expressions

    const shapesArray = getShapesArray()
    return shapesArray.reduce((largestShape, shape) => {
      if (shape.calculated > largestShape.calculated) {
        return shape
      }
      return largestShape
    }, shapesArray.length ? shapesArray[0] : { calculated: 0 })
  })

  const currentShape = computed(() => {
    // Force recomputation when version changes
    shapesVersion.value // eslint-disable-line no-unused-expressions

    const shapesArray = getShapesArray()
    let shape = shapesArray.find(s => s.selected)
    return (shape) ? shape : { indexID: null }
  })

  const filteredUnits = computed(() => {
    return units.filter(u => u.type == activeStatusTabName.value && u.group == ((imperialStatus.value == true) ? 'imperial' : 'metric'))
  })

  const primaryUnit = computed(() => {
    const activeTab = activeStatusTabName.value
    if (!primaryUnits.value[activeTab] || !units.find(u => u.name == primaryUnits.value[activeTab].name)) {
      return units.find(u => u.name == currentDefaultUnit.value.name)
    }
    return primaryUnits.value[activeTab]
  })

  const currentDefaultUnit = computed(() => {
    return units.find(u => u.name == default_units[activeStatusTabName.value] && u.type == activeStatusTabName.value)
  })

  const currentBaseUnit = computed(() => {
    return units.find(u => u.name == base_units[activeStatusTabName.value] && u.type == activeStatusTabName.value)
  })

  const totalShapeCalculatedConverted = computed(() => {
    let rawCalculated = totalShapeCalculatedRaw.value
    let converted = roundTo(convert(rawCalculated).from(currentBaseUnit.value.name).to(primaryUnit.value.name), primaryUnit.value.decimals)
    return converted
  })

  const totalShapeCalculatedRaw = computed(() => {
    // Force recomputation when version changes
    shapesVersion.value // eslint-disable-line no-unused-expressions

    const shapesArray = getShapesArray()
    let shape = shapesArray.reduce((accum, current) => {
      if (current.type == activeStatusTabName.value) {
        return accum + current.calculated
      }
      return accum
    }, 0)
    return shape
  })

  const activeStatusTabName = computed(() => {
    return (activeStatusTab.value == 0 || activeStatusTab.value == null) ? 'area' : 'distance'
  })

  // ===========================
  // Actions
  // ===========================

  async function initializeStore({ mapId } = {}) {
    if (mapId) {
      const { useMapSharingStore } = await import('./mapSharing')
      const mapSharingStore = useMapSharingStore()

      await resetState()

      const { data, mapId: returnMapId } = await mapSharingStore.getSharedMap({ mapId })
      _initializeFromData(data)
      mapSharingStore.mapId = returnMapId

      if (data.hasOwnProperty('meta')) {
        const { useContactStore } = await import('./contact')
        const contactStore = useContactStore()

        const properties = {
          'customerFirstName': 'firstName',
          'customerLastName': 'lastName',
          'customerEmail': 'email',
          'customerPhone': 'phone',
          'customerProductInterest': 'productInterest',
          'projectName': 'projectName'
        }

        Object.keys(properties).forEach(property => {
          const key = properties[property]
          if (data.meta.hasOwnProperty(property)) {
            if (key === 'projectName') {
              projectName.value = data.meta[property]
            } else {
              contactStore[key] = data.meta[property]
            }
          }
        })
      }
    } else {
      _initializeFromData()
    }
  }

  function _initializeFromData(newState = null) {
    // If newState is provided (from shared map), use it. Otherwise, load from localStorage
    if (newState || localStorage.getItem('store')) {
      let storageState = newState ? {} : JSON.parse(localStorage.getItem('store') || '{}')

      let loadedState = {
        ...storageState,
        ...(newState) ? newState : {}
      }

      // Migrate old 'areas' property to 'shapes'
      if (loadedState.hasOwnProperty('areas')) {
        loadedState.shapes = loadedState.areas
        delete loadedState.areas
      }

      // Remove old primaryUnit property
      if (loadedState.hasOwnProperty('primaryUnit')) {
        delete loadedState.primaryUnit
      }

      let loadedShapes = loadedState.shapes ? loadedState.shapes.map((s) => defaults({...s, selected: false }, shapeDefaults)) : []

      // Apply loaded state
      mode.value = 'move'
      toolType.value = loadedState.toolType || 'area'
      imperial.value = loadedState.imperial !== undefined ? loadedState.imperial : true
      primaryUnits.value = {}
      activeStatusTab.value = 0
      address.value = loadedState.address || ''
      displayAddress.value = loadedState.displayAddress || ''

      // NEW: Load shapes into Map instead of array
      shapesData.clear()
      loadedShapes.forEach(shape => {
        shapesData.set(shape.indexID, markRaw(shape))
      })
      shapesVersion.value++ // Trigger sync

      lastMapClickEvent.value = null
      tutorialModalVisible.value = false
      globalZIndex.value = loadedState.globalZIndex || 0
      mapCenter.value = {
        fromSave: (loadedState.mapCenter) ? loadedState.mapCenter.fromSave : false,
        fromMap: false,
        lat: (loadedState.mapCenter) ? loadedState.mapCenter.lat : null,
        lng: (loadedState.mapCenter) ? loadedState.mapCenter.lng : null
      }
      meta.value = loadedState.meta || {}
      mapDrag.value = false
      toolbarsHidden.value = false
      mapZoom.value = {
        fromMap: false,
        number: loadedState.mapZoom?.number
      }
      mapEditingEnabled.value = loadedState.mapEditingEnabled !== undefined ? loadedState.mapEditingEnabled : true
      projectName.value = loadedState.projectName || ''
      currentControlOverlay.value = null
      loading.value = false
      appClicked.value = false
      // Preserve UTM from URL if already set (URL takes precedence over localStorage)
      const currentUtm = utm.value
      const hasUrlUtm = currentUtm && Object.values(currentUtm).some(v => v)
      utm.value = hasUrlUtm ? currentUtm : (loadedState.utm || {})
      uuid.value = loadedState.uuid ? loadedState.uuid : uuidv4()
    }
  }

  function searchAddress(addressStr) {
    address.value = null
    mapCenter.value = null
    setTimeout(() => {
      address.value = addressStr
      displayAddress.value = addressStr
    }, 500)
  }

  function setDisplayAddress(addressStr) {
    displayAddress.value = addressStr
  }

  // REMOVED: refreshShape - now handled by command queue

  function hoverShape(shape) {
    // NEW: Optimized to only update changed shapes, not all shapes
    const targetID = shape?.indexID

    for (const [indexID, shapeState] of shapesData.entries()) {
      const shouldHighlight = targetID === indexID
      if (shapeState.highlighted !== shouldHighlight) {
        shapesData.set(indexID, markRaw({
          ...shapeState,
          highlighted: shouldHighlight
        }))
      }
    }

    shapesVersion.value++
    triggerSave()
  }

  function setMode(newMode) {
    mode.value = newMode
  }

  function setCurrentControlOverlay(payload) {
    if (payload.hasOwnProperty('current')) {
      if (get(currentControlOverlay.value, 'current') && currentControlOverlay.value.uuid != payload.uuid) {
        currentControlOverlay.value.current.setVisible(false)
      }
      currentControlOverlay.value = payload
    }
  }

  function setToolType(type) {
    toolType.value = type
  }

  function setMapCenter(latLng) {
    if (latLng && latLng.lat && latLng.lng) {
      mapCenter.value = {
        lat: typeof latLng.lat === 'function' ? latLng.lat() : latLng.lat,
        lng: typeof latLng.lng === 'function' ? latLng.lng() : latLng.lng,
        fromMap: latLng.fromMap || false,
        fromSave: latLng.fromSave || false
      }
    } else {
      mapCenter.value = null
    }
  }

  function setMapDrag(dragging) {
    mapDrag.value = dragging
  }

  function setToolbarsHidden(hidden) {
    toolbarsHidden.value = hidden
  }

  function setShapeCutout(cfg) {
    if (cfg.shape && cfg.hasOwnProperty('cutout')) {
      const indexID = cfg.shape.indexID
      const shapeState = shapesData.get(indexID)
      if (shapeState) {
        shapesData.set(indexID, markRaw({
          ...shapeState,
          cutout: cfg.cutout
        }))
        shapesVersion.value++
        triggerSave()
      }
    }
  }

  function setShapeDistanceVisible(cfg) {
    if (cfg.shape && cfg.hasOwnProperty('visible')) {
      const indexID = cfg.shape.indexID
      const shapeState = shapesData.get(indexID)
      if (shapeState) {
        shapesData.set(indexID, markRaw({
          ...shapeState,
          distanceVisible: cfg.visible
        }))
        shapesVersion.value++
        triggerSave()
      }
    }
  }

  function setShapeLabelText(cfg) {
    if (cfg.shape && cfg.hasOwnProperty('labelText')) {
      const indexID = cfg.shape.indexID
      const shapeState = shapesData.get(indexID)
      if (shapeState) {
        shapesData.set(indexID, markRaw({
          ...shapeState,
          labelText: cfg.labelText
        }))
        shapesVersion.value++
        triggerSave()
      }
    }
  }

  function setCurrentShape(shape) {
    // NEW: Optimized to only update changed shapes
    const targetID = shape?.indexID

    for (const [indexID, shapeState] of shapesData.entries()) {
      const shouldSelect = targetID === indexID
      if (shapeState.selected !== shouldSelect) {
        shapesData.set(indexID, markRaw({
          ...shapeState,
          selected: shouldSelect
        }))
      }
    }

    shapesVersion.value++
    triggerSave()
  }

  async function deleteShape({ shape, skipConfirm = false } = {}) {
    if (shape) {
      const { useDialogsStore } = await import('./dialogs')
      const dialogsStore = useDialogsStore()

      // Handle both shape instances (with methods) and plain state objects
      const labelText = shape.labelText || (typeof shape.getLabelText === 'function' ? shape.getLabelText() : '')
      const type = shape.type || (typeof shape.getType === 'function' ? shape.getType() : '')

      const removeFn = () => {
        const indexID = get(shape, 'indexID', shape)
        if (shapesData.has(indexID)) {
          shapesData.delete(indexID)
          shapesVersion.value++
          triggerSave()
        }

        setCurrentShape(null)
        setMode('move')
        resetDisplayIndexes()
      }

      if (shape.indexID && !skipConfirm) {
        dialogsStore.confirm(
          0,
          ((labelText.length > 0) ? `Are you sure you want to delete the <b>${labelText}</b> ${type} measurement?` : `Are you sure you want to delete this ${type} measurement?`) + ' This cannot be undone.',
          {
            title: 'Confirm',
            confirmText: 'Delete',
            cancelText: 'Don\'t delete',
            type: 'is-danger',
            onConfirm: removeFn
          }
        )
        return
      }

      removeFn()
    }
  }

  function clearShapes() {
    shapesData.clear()
    shapesVersion.value++
    triggerSave()
  }

  function setProjectName(name) {
    projectName.value = name
  }

  async function resetState() {
    const { useMapSharingStore } = await import('./mapSharing')
    const { useDialogsStore } = await import('./dialogs')
    const { useContactStore } = await import('./contact')

    const mapSharingStore = useMapSharingStore()
    const dialogsStore = useDialogsStore()
    const contactStore = useContactStore()

    localStorage.removeItem('store')

    mode.value = 'move'
    toolType.value = 'area'
    imperial.value = true
    primaryUnits.value = {}
    activeStatusTab.value = 0
    address.value = ''
    displayAddress.value = ''
    shapesData.clear()
    shapesVersion.value = 0
    meta.value = {}
    projectName.value = ''
    lastMapClickEvent.value = null
    globalZIndex.value = 0
    mapCenter.value = {}
    mapZoom.value = {}
    currentControlOverlay.value = null
    loading.value = false
    appClicked.value = false
    uuid.value = uuidv4()

    await mapSharingStore.resetState()
    await dialogsStore.resetState()
    await contactStore.resetState()
    await saveLocalStorageState()
  }

  function initNewShape(shape) {
    // Use __isNewArea property instead of instanceof
    for (const [indexID, shapeState] of shapesData.entries()) {
      if (shapeState.__isNewArea === true) {
        shapesData.set(indexID, markRaw(shape))
        shapesVersion.value++
        triggerSave()
        return
      }
    }
  }

  function addShape(shape) {
    if (shape) {
      // Generate indexID if not present (for NEW_AREA objects)
      const indexID = shape.indexID || new Date().getTime()
      const shapeWithLabel = { ...shape, indexID, labelText: 'Area' }
      shapesData.set(indexID, markRaw(shapeWithLabel))
      shapesVersion.value++
      resetDisplayIndexes()
    }
  }

  function setMapEditingEnabled(enabled) {
    for (const [indexID, shapeState] of shapesData.entries()) {
      shapesData.set(indexID, markRaw({
        ...shapeState,
        editingEnabled: enabled
      }))
    }
    shapesVersion.value++
    triggerSave()
  }

  function setTutorialModalVisibility(visibility) {
    tutorialModalVisible.value = (visibility === true) ? true : false
  }

  function increaseGlobalZIndex() {
    globalZIndex.value++
  }

  function setImperialState(status) {
    imperial.value = status
  }

  function setPrimaryUnit(unitPayload) {
    if (!unitPayload.name || !units.find(u => unitPayload.name == u.name && unitPayload.type == u.type)) {
      unitPayload = currentDefaultUnit.value
    }
    primaryUnits.value[unitPayload.type] = unitPayload
  }

  function setLastMapClickEvent({ event, delayedClear = false }) {
    lastMapClickEvent.value = event
    if (delayedClear) {
      setTimeout(() => lastMapClickEvent.value = null, 500)
    }
  }

  function setActiveStatusTab(tab) {
    activeStatusTab.value = tab
  }

  function setAppClicked() {
    appClicked.value = true
    setTimeout(() => appClicked.value = false, 100)
  }

  function setUtm(utmData) {
    utm.value = utmData
  }

  function setMeta(metaData) {
    meta.value = metaData
  }

  function setMapZoom(zoom) {
    if (zoom.number) {
      mapZoom.value = zoom
    }
  }

  function resetDisplayIndexes() {
    const shapesArray = Array.from(shapesData.values())
    shapesArray.forEach((s, idx) => {
      shapesData.set(s.indexID, markRaw({
        ...s,
        displayIndex: idx + 1
      }))
    })
    shapesVersion.value++
    triggerSave()
  }

  function setLoading(isLoading, message = '') {
    loading.value = isLoading ? true : false
    loadingMessage.value = isLoading ? message : ''
  }

  function saveLocalStorageState() {
    const state = {
      mode: mode.value,
      toolType: toolType.value,
      imperial: imperial.value,
      primaryUnits: primaryUnits.value,
      activeStatusTab: activeStatusTab.value,
      address: address.value,
      displayAddress: displayAddress.value,
      shapes: getShapesArray(), // Convert Map to Array for serialization
      lastMapClickEvent: lastMapClickEvent.value,
      tutorialModalVisible: tutorialModalVisible.value,
      globalZIndex: globalZIndex.value,
      mapCenter: mapCenter.value,
      meta: meta.value,
      mapDrag: mapDrag.value,
      toolbarsHidden: toolbarsHidden.value,
      mapZoom: mapZoom.value,
      mapEditingEnabled: mapEditingEnabled.value,
      projectName: projectName.value,
      loading: loading.value,
      appClicked: appClicked.value,
      utm: utm.value,
      uuid: uuid.value
    }

    let copy = omit(state, ['currentControlOverlay'])
    localStorage.setItem('store', JSON.stringify(copy))
  }

  // Instance-scoped throttled save helper
  const throttledSave = throttle(saveLocalStorageState, 1000)

  // Helper to trigger throttled save (called after shape modifications)
  function triggerSave() {
    throttledSave()
  }

  // Set triggerSave on command queue now that it's defined
  commandQueue.store.triggerSave = triggerSave

  return {
    // State
    mode,
    toolType,
    imperial,
    primaryUnits,
    activeStatusTab,
    address,
    displayAddress,
    shapesData, // NEW: Non-reactive Map
    shapesVersion, // NEW: Version counter for change detection
    getShapesArray, // NEW: Helper function to get shapes array (NOT reactive)
    commandQueue, // NEW: Command queue for shape updates
    lastMapClickEvent,
    tutorialModalVisible,
    globalZIndex,
    mapCenter,
    meta,
    mapDrag,
    toolbarsHidden,
    mapZoom,
    mapEditingEnabled,
    projectName,
    currentControlOverlay,
    loading,
    loadingMessage,
    appClicked,
    utm,
    uuid,

    // Getters
    shapesWithConversions,
    measureSuffix,
    imperialStatus,
    lastMapClickLatLng,
    largestShape,
    currentShape,
    filteredUnits,
    primaryUnit,
    currentDefaultUnit,
    currentBaseUnit,
    totalShapeCalculatedConverted,
    totalShapeCalculatedRaw,
    activeStatusTabName,

    // Actions
    initializeStore,
    searchAddress,
    setDisplayAddress,
    // refreshShape REMOVED - now handled by command queue
    hoverShape,
    setMode,
    setCurrentControlOverlay,
    setToolType,
    setMapCenter,
    setMapDrag,
    setToolbarsHidden,
    setShapeCutout,
    setShapeDistanceVisible,
    setShapeLabelText,
    setCurrentShape,
    deleteShape,
    clearShapes,
    setProjectName,
    resetState,
    initNewShape,
    addShape,
    setMapEditingEnabled,
    setTutorialModalVisibility,
    increaseGlobalZIndex,
    setImperialState,
    setPrimaryUnit,
    setLastMapClickEvent,
    setActiveStatusTab,
    setAppClicked,
    setUtm,
    setMeta,
    setMapZoom,
    resetDisplayIndexes,
    setLoading,
    saveLocalStorageState,
    triggerSave
  }
}, {
  persist: false  // Disabled - using manual persistence via saveLocalStorageState
})
